import { m } from "@plannello/i18n/messages";
import type { ItemCreate, ItemLite, QuerySort } from "@plannello/shared";
import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useMemo } from "react";
import { Button, EmptyState, InlineError, RowSkeleton } from "@/components/ui";
import { parseColumns, parseDensity } from "@/components/views/viewState";
import { useCatalog } from "@/data/CatalogProvider";
import { type ItemsResult, useItems } from "@/data/items";
import { useMe } from "@/lib/queries";
import { Board } from "./Board";
import { childStats } from "./display";
import { buildGroups, type Group, type GroupBy, parseGroupBy } from "./grouping";
import { ItemList } from "./ItemList";
import { openQuickCreate, patchToCreate } from "./quickCreateStore";
import { useReadOnly } from "./readOnly";
import { parseSortParam, useSetViewSearch, type ViewSearch } from "./viewSearch";

export const EMPTY_LITE: ItemLite = {
  id: "",
  key: "",
  productId: "",
  spaceId: "",
  number: 0,
  typeId: "",
  statusId: "",
  priority: 0,
  title: "",
  parentId: null,
  milestoneId: null,
  rank: "",
  assigneeIds: [],
  labelIds: [],
  blockedByIds: [],
  estimate: null,
  startDate: null,
  dueDate: null,
  fields: {},
  reporterId: null,
  createdById: null,
  createdAt: "",
  updatedAt: "",
  statusChangedAt: null,
  startedAt: null,
  completedAt: null,
  archivedAt: null,
  version: 1,
};

export interface ItemsViewProps {
  /** Products in scope (a product view: one; My work and Triage: every visible product). */
  productIds: string[];
  /** Fixed part of the query that the filter bar does not show (e.g. "assignee:me is:open"). */
  baseQuery?: string;
  layout: "list" | "board";
  search: ViewSearch;
  defaultGroup: GroupBy;
  defaultSort?: QuerySort[];
  /** Grid accessible name. */
  label: string;
  /** Key for remembered collapsed groups. */
  viewKey: string;
  /** Shown when the scope has no items at all (before any filter). */
  empty: ReactNode;
  /** Quick-create defaults for this view (product). */
  createDefaults?: () => Partial<ItemCreate>;
  /** Called once results are known, e.g. to show counts in the header. */
  children?: (r: ItemsResult) => ReactNode;
}

/**
 * The body of every item view: runs the query (client-side, see data/items.ts), groups the
 * result and renders the list or the board with the four A6 states (cold skeleton, error with
 * retry, empty, filtered to nothing).
 */
export function ItemsView({
  productIds,
  baseQuery,
  layout,
  search,
  defaultGroup,
  defaultSort,
  label,
  viewKey,
  empty,
  createDefaults,
  children,
}: ItemsViewProps) {
  const catalog = useCatalog();
  const readOnly = useReadOnly();
  const me = useMe().data;
  const navigate = useNavigate();
  const setSearch = useSetViewSearch();
  const userQuery = search.q ?? "";
  const queryText = [baseQuery, userQuery].filter(Boolean).join(" ");
  const sort = useMemo(() => parseSortParam(search.sort) ?? defaultSort, [search.sort, defaultSort]);
  const result = useItems(queryText, productIds, { sort });
  const groupBy = parseGroupBy(search.group, catalog, defaultGroup);
  const boardGroup: GroupBy = groupBy === "none" || groupBy === "product" ? "status" : groupBy;

  const stats = useMemo(() => childStats(result.all.items, catalog), [result.all.items, catalog]);
  // Display options (list): sub-items as a tree or hidden, sub-grouping, properties, density.
  const showSubItems = search.subs !== "off";
  const subRaw = layout === "list" && groupBy !== "none" ? parseGroupBy(search.sub, catalog, "none") : "none";
  const subGroupBy: GroupBy = subRaw === groupBy || subRaw === "product" ? "none" : subRaw;
  const columns = useMemo(() => parseColumns(search.cols), [search.cols]);
  const density = parseDensity(search.density);
  const visible = useMemo(
    () => (showSubItems ? result.items : result.items.filter((i) => !i.parentId || !result.all.byId.has(i.parentId))),
    [result.items, result.all.byId, showSubItems],
  );
  const workflowIds = useMemo(
    () => [...new Set(productIds.map((id) => catalog.productById.get(id)?.workflowId).filter((w): w is string => !!w))],
    [productIds, catalog],
  );
  const groups = useMemo(() => {
    const opts = { workflowIds, meId: me?.id ?? null, productIds };
    const top = buildGroups(visible, layout === "board" ? boardGroup : groupBy, catalog, {
      ...opts,
      includeEmpty: layout === "board",
    });
    if (subGroupBy === "none") return top;
    return top.map(
      (g): Group => ({
        ...g,
        children: buildGroups(g.items, subGroupBy, catalog, opts).map((sg) => ({
          ...sg,
          // Creating or moving into a sub-group sets both values.
          patchFor: (item) => {
            const a = g.patchFor(item);
            const b = sg.patchFor(item);
            return a || b ? { ...(a ?? {}), ...(b ?? {}) } : null;
          },
        })),
      }),
    );
  }, [visible, groupBy, subGroupBy, boardGroup, layout, catalog, workflowIds, me?.id, productIds]);

  const open = (item: ItemLite) => void navigate({ to: "/$itemKey", params: { itemKey: item.key } });
  const peek = (item: ItemLite) => setSearch({ peek: search.peek === item.key ? undefined : item.key });

  const header = children?.(result);

  if (result.isPending && result.items.length === 0) {
    return (
      <>
        {header}
        <RowSkeleton rows={8} />
      </>
    );
  }
  if (result.isError && result.items.length === 0) {
    return (
      <>
        {header}
        <div className="px-5 py-4 sm:px-8">
          <InlineError message={m.error_network()} onRetry={result.refetch} />
        </div>
      </>
    );
  }
  if (result.items.length === 0 && !userQuery) {
    return (
      <>
        {header}
        <div className="px-5 pt-6 sm:px-8">{empty}</div>
      </>
    );
  }
  if (result.items.length === 0 && layout === "list") {
    return (
      <>
        {header}
        <div className="px-5 pt-6 sm:px-8">
          <EmptyState
            title={m.filter_empty_title()}
            description={m.filter_empty_body({ query: userQuery || queryText })}
            action={
              userQuery ? (
                <Button variant="secondary" onPress={() => setSearch({ q: undefined })}>
                  {m.filter_clear()}
                </Button>
              ) : undefined
            }
          />
        </div>
      </>
    );
  }

  const groupDefaults = (g: { patchFor: (i: ItemLite) => unknown } | null) =>
    ({
      ...(createDefaults?.() ?? {}),
      ...patchToCreate(g ? (g.patchFor(EMPTY_LITE) as never) : null),
    }) as Partial<ItemCreate>;

  return (
    <>
      {header}
      {layout === "list" ? (
        <ItemList
          groups={groups}
          showHeaders={groupBy !== "none"}
          catalog={catalog}
          childStats={stats}
          label={label}
          peekId={search.peek ? (result.items.find((i) => i.key === search.peek)?.id ?? null) : null}
          onOpen={open}
          onPeek={peek}
          onEscape={search.peek ? () => setSearch({ peek: undefined }) : undefined}
          onFocusItem={(item) => {
            if (search.peek && search.peek !== item.key) setSearch({ peek: item.key });
          }}
          createDefaults={groupDefaults}
          onCreateInGroup={readOnly ? undefined : (g) => openQuickCreate(groupDefaults(g))}
          viewKey={viewKey}
          tree={showSubItems}
          columns={columns}
          density={density}
          itemById={(id) => result.all.byId.get(id)}
        />
      ) : (
        <Board
          groups={groups}
          groupBy={boardGroup}
          catalog={catalog}
          childStats={stats}
          label={label}
          peekKey={search.peek ?? null}
          onOpen={open}
          onPeek={peek}
          onEscape={search.peek ? () => setSearch({ peek: undefined }) : undefined}
          createDefaults={groupDefaults}
          onCreateInGroup={readOnly ? undefined : (g) => openQuickCreate(groupDefaults(g))}
        />
      )}
    </>
  );
}
